import {useTranslation} from "react-i18next";
import React, {useState} from "react";
import {Box, IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip} from "@mui/material";
import ReactCountryFlag from "react-country-flag";

// Map language codes to country codes for flags
const Flags = {
  "en-US": 'GB',
  "en": 'GB',
  "de": 'DE',
  "fr": 'FR',
  "it": 'IT',
  "hr": 'HR',
  "es": 'ES',
}

// Fallback logic to get a valid country code
const getCountryCode = (langCode) => {
  if (!langCode) return 'GB';
  // Try direct match
  if (Flags[langCode]) return Flags[langCode];
  // Try base language code (e.g., 'en-US' -> 'en')
  const baseCode = langCode.split('-')[0];
  if (Flags[baseCode]) return Flags[baseCode];
  // Default fallback
  return 'GB';
}

export const LanguageSelection = ({ sx }) => {
  const { t, i18n } = useTranslation('general');
  const [anchorEl, setAnchorEl] = useState(null);

  const languages = [
    {code: 'en', name: t('languageSelection.languages.english'), flag: '🇬🇧'},
    {code: 'de', name: t('languageSelection.languages.german'), flag: '🇩🇪'},
    {code: 'fr', name: t('languageSelection.languages.french'), flag: '🇫🇷'},
    {code: 'it', name: t('languageSelection.languages.italian'), flag: '🇮🇹'},
    {code: 'hr', name: t('languageSelection.languages.croatian'), flag: '🇭🇷'},
    {code: 'es', name: t('languageSelection.languages.spanish'), flag: '🇪🇸'}
  ];

  const handleLanguageClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageChange = (code) => {
    i18n.changeLanguage(code);
    handleClose();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Box sx={{
      ...sx,
      zIndex: (theme) => theme.components.MuiLayout.defaultProps.zIndex.header + 1
    }}>
      <Tooltip title={t('languageSelection.tooltip')}>
        <IconButton
          onClick={handleLanguageClick}
          size="small"
          sx={{
            width: { xs: 32, sm: 40 },
            height: { xs: 32, sm: 40 },
            padding: { xs: '4px', sm: '8px' },
            border: '1px solid transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Make sure icon is visible on all devices
            '& svg': {
              display: 'block',
              width: '100%',
              height: 'auto'
            }
          }}
        >
          <ReactCountryFlag 
            countryCode={getCountryCode(i18n.language)} 
            svg 
            style={{ 
              width: '100%', 
              height: 'auto',
              minWidth: '16px', // Ensure minimum size on small screens
            }}
            title={i18n.language}
            aria-label={`${i18n.language} language flag`}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              minWidth: "200px", 
              bgcolor: "#fefefe", 
              borderRadius: 2, 
              mt: 1,
              boxShadow: 2,
              // Ensure menu displays properly on small screens
              maxWidth: 'calc(100% - 16px)', // Prevent overflowing screen width
              maxHeight: { xs: '70vh', sm: 'auto' }, // Limit height on mobile
            }
          }
        }}
        disableScrollLock={false} // Enable scroll lock to prevent background scrolling
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {languages.map((lang) => (
          <MenuItem 
            key={lang.code} 
            onClick={() => handleLanguageChange(lang.code)}
            sx={{
              py: 1,
              px: 2,
              '&:hover': {
                bgcolor: 'action.hover'
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ReactCountryFlag 
                countryCode={getCountryCode(lang.code)} 
                svg 
                style={{
                  width: '20px',
                  height: 'auto',
                  display: 'block'
                }} 
              />
            </ListItemIcon>
            <ListItemText primary={lang?.name} />
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};